import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType, type Location } from 'react-router-dom';
import {
  getPageShellKey,
  getPageTransitionClass,
} from '../lib/navigation';
import {
  getRememberedScrollPosition,
  registerScrollContainer,
  rememberScrollPosition,
  setRememberedScrollPosition,
} from '../lib/scroll-position-store';
import { resolveScrollRestore } from '../lib/scroll-restoration';
import {
  getSectionScrollTarget,
  hasSectionParam,
} from '../lib/scroll-intent';

export { getPageShellKey, getPageTransitionClass };

const applyScrollTop = (el: HTMLElement, scrollTop: number) => {
  el.scrollTop = scrollTop;
};

const restoreScrollTop = (el: HTMLElement, key: string) => {
  const target = getRememberedScrollPosition(key);
  applyScrollTop(el, target);

  // Content height can settle after the first paint (cached queries, layout).
  requestAnimationFrame(() => {
    applyScrollTop(el, target);
    requestAnimationFrame(() => applyScrollTop(el, target));
  });
};

export const useScrollRestoration = (scrollRef: React.RefObject<HTMLElement | null>) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevLocation = useRef<Location | null>(null);

  useEffect(() => {
    registerScrollContainer(scrollRef.current);
    return () => registerScrollContainer(null);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const pathname = location.pathname;
    const persist = () => {
      setRememberedScrollPosition(pathname, el.scrollTop);
    };

    persist();
    el.addEventListener('scroll', persist, { passive: true });
    return () => {
      el.removeEventListener('scroll', persist);
      rememberScrollPosition(pathname);
    };
  }, [location.pathname, scrollRef]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const prev = prevLocation.current;
    const prevPath = prev?.pathname ?? '';
    const prevSearch = prev?.search ?? '';
    const nextPath = location.pathname;
    const nextSearch = location.search;

    if (prevPath === nextPath && hasSectionParam(prevSearch) && !hasSectionParam(nextSearch)) {
      prevLocation.current = location;
      return;
    }

    const sectionTarget = getSectionScrollTarget(nextSearch);
    if (sectionTarget) {
      prevLocation.current = location;
      return;
    }

    const decision = resolveScrollRestore(prevPath, nextPath, navigationType);

    if (decision.action === 'top') {
      applyScrollTop(el, 0);
    } else if (decision.action === 'restore') {
      restoreScrollTop(el, decision.key);
    }

    prevLocation.current = location;
  }, [location, navigationType, scrollRef]);
};

/** Call right before route change so scroll is captured before DOM swap. */
export const captureScrollBeforeNavigation = (fromPath: string) => {
  rememberScrollPosition(fromPath);
};
